import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { DragSource } from 'react-dnd';
import { STATUS_ACCEPTED, STATUS_REJECTED } from '../../core/utils/constants';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import ConceptSlat from './ConceptSlat';
import TaxonomyStructure from './TaxonomyStructure';

const REJECTED = STATUS_REJECTED;
const APPROVED = STATUS_ACCEPTED;

const styles = Object.assign({
    concepts: {
        marginLeft: '30px'
    }
});

const Types = {
    CONCEPT: 'concept'
};

const conceptSource = {
    beginDrag(props) {
        return {
            conceptId: props.id,
            label: props.data.prefLabel && props.data.prefLabel[props.defaultLang] ? props.data.prefLabel[props.defaultLang] : null,
            type: "Concept"
        };
    }
};

function collectSource(connect) {
    return {
        connectDragSource: connect.dragSource()
    };
}

class Concept extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            scrollTime: null,
            warning: ""
        };
    }

    shouldComponentUpdate(nextProps) {
        // Each time a concept is expanded/collapsed the number of paths to it changes
        // If they differ we need to rerender as either that node or one of its children needs drawing
        if (nextProps.prefLabel && !this.props.prefLabel) return true;
        if (nextProps.viewLang !== this.props.viewLang) return true;
        if (nextProps.isFullView !== this.props.isFullView) return true;
        if (nextProps.useCustomSort !== this.props.useCustomSort) return true;
        const firstInOldPath = this.props.selectedTitlePath.length > 0 ? this.props.selectedTitlePath[0] : null;
        const firstInNewPath = nextProps.selectedTitlePath.length > 0 ? nextProps.selectedTitlePath[0] : null;
        if (firstInOldPath === this.props.id || firstInNewPath === this.props.id) return true;
        const nextPropsExpansion = nextProps.expandedPaths;
        const currentPropsExpansion = this.props.expandedPaths;
        if (nextProps.foundTime !== this.props.foundTime && nextProps.foundPath.startsWith(nextProps.path)) return true;
        if (nextProps.version === this.props.version && nextPropsExpansion.length === currentPropsExpansion.length) return false;
        return true;
    }

    expandConcept = () => this.props.expandConcept(this.props.path);

    collapseConcept = () => this.props.collapseConcept(this.props.path);

    processExpandAndScroll() {
        if (this.props.foundRef === null || this.props.data.pendingDeletion
            || this.props.foundPath !== this.props.path
            || this.state.scrollTime === this.props.foundTime) return;
        this.setState({ scrollTime: this.props.foundTime }, () => {
            if (this.props.foundRef.current) {
                this.props.foundRef.current.scrollIntoView({
                    block: 'center',
                    inline: 'center',
                });
            }
        });
    }

    componentDidMount() {
        const isExpanded = this.props.expandedPaths.includes(this.props.path);
        if (isExpanded && !this.props.data.narrower) this.expandConcept();
        this.processExpandAndScroll();
    }

    componentDidUpdate() {
        const isExpanded = this.props.expandedPaths.includes(this.props.path);
        if (isExpanded && !this.props.data.narrower) this.expandConcept();
        else this.processExpandAndScroll();
    }

    checkConcept = async (id) => {
        let warning = "";
        if (this.props.data.id === id) {
            warning = "You cannot add relations where a concept is related to itself.";
        }
        else {
            // Need to ensure fully loaded
            await GS.loadData([this.props.data.id], {}, this.props.graph);
            const data = GS.data(this.props.data.id, this.props.graph);
            if (data.related && data.related.includes(id)) {
                warning = `You cannot add a concept that is already related to this concept.`;
            }
            else if (data.narrower && data.narrower.includes(id)) {
                warning = `You cannot add a concept that is already narrower to this concept.`;
            }
        }
        if (warning) {
            this.props.showWarning(warning);
        }
        else {
            const titlePathPlus = this.props.titlePath && this.props.titlePath.concat(this.props.data.id);
            this.props.onAddExistingConcept(titlePathPlus, this.props.path)(this.props.data.id, "narrower", id);
        }
    };

    onExitWarning = () => this.setState({ warning: "" });

    render() {

        const { connectDragSource, path, onBreakLink, parent, defaultLang, schemeIRI, childProperty,
            foundPath, foundRef, selectedTitlePath, useSlashIRI, useUuidIRI, iriOptions, useWorkflow, useCounterIRI,
            classes, data, onSelect, expandedPaths, foundTime, expandConcept, titleTree, titlePath, viewLang, counters,
            graph, activeConcept, showWarning, isFullView, useCustomSort, customSort, project,
            collapseConcept, onAddExistingConcept, onAddNewConcept, onDeleteConcept,
            addToScheme, skosXL, useXL } = this.props;

        if (data.pendingDeletion) return null;
        const isReadOnly = this.props.isReadOnly
            || (useWorkflow && "status" in data && (data.status[0] === APPROVED || data.status[0] === REJECTED));

        const isExpanded = expandedPaths && expandedPaths.includes(path);
        const resourceCount = data.narrower ? data.narrower.length : (
            data.resourceCount || 0
        );

        let label = data.id;
        const displayLang = viewLang && data.prefLabel && data.prefLabel[viewLang] ?
            viewLang
            :
            data.prefLabel && data.prefLabel[defaultLang] ?
                defaultLang
                :
                "und";
        if (data.prefLabel && data.prefLabel[displayLang]) {
            label = Array.isArray(data.prefLabel[displayLang]) ?
                [...data.prefLabel[displayLang]].sort().join(" AND ") : data.prefLabel[displayLang];
        }

        let title = label;
        let titlePathPlus = titlePath && titlePath.concat(data.id);
        let titleTreePlus = titleTree && titleTree.concat([title]);

        const slat = <div data-test={"slat-container-" + data.id}>
            <ConceptSlat
                key={"CS-" + data.id}
                activeConcept={activeConcept}
                expandConcept={this.expandConcept}
                collapseConcept={this.collapseConcept}
                isExpanded={isExpanded}
                schemeIRI={schemeIRI}
                useCustomSort={useCustomSort}
                customSort={customSort}
                iriOptions={iriOptions}
                defaultLang={defaultLang}
                viewLang={viewLang}
                checkConcept={this.checkConcept}
                useXL={useXL}
                useWorkflow={useWorkflow}
                useSlashIRI={useSlashIRI}
                useUuidIRI={useUuidIRI}
                useCounterIRI={useCounterIRI}
                counters={counters}
                onAddNewConcept={onAddNewConcept(titlePathPlus, path)}
                onAddExistingConcept={onAddExistingConcept(titlePathPlus, path)}
                data={data}
                graph={graph}
                selectedTitlePath={selectedTitlePath}
                titlePath={titlePathPlus}
                titleTree={titleTreePlus}
                path={path}
                parent={parent}
                childProperty={childProperty}
                onSelect={onSelect}
                addToScheme={addToScheme}
                onBreakLink={onBreakLink}
            />
        </div>;

        return <div className={classes.slatContainer}>
            {foundRef && foundPath === path && <div ref={foundRef} />}
            {isReadOnly || (data.deprecated === true) ? slat : connectDragSource(slat)}
            {isExpanded && resourceCount > 0 && resourceCount < 50000 &&
                <div data-test="child-nodes" key={"TS-" + data.id} className={classes.concepts}>
                    <TaxonomyStructure
                        id={data.id}
                        displayAll={false}
                        graph={graph}
                        iriOptions={iriOptions}
                        useCustomSort={useCustomSort}
                        customSort={customSort}
                        version={data._version}
                        defaultLang={defaultLang}
                        viewLang={viewLang}
                        skosXL={skosXL}
                        project={project}
                        useXL={useXL}
                        isFullView={isFullView}
                        useWorkflow={useWorkflow}
                        useSlashIRI={useSlashIRI}
                        useUuidIRI={useUuidIRI}
                        showWarning={showWarning}
                        activeConcept={activeConcept}
                        childProperty='narrower'
                        foundPath={foundPath}
                        foundTime={foundTime}
                        foundRef={foundRef}
                        concepts={data.narrower}
                        path={path}
                        selectedTitlePath={selectedTitlePath.length > 0 && selectedTitlePath[0] === this.props.id ? selectedTitlePath.slice(1) : []}
                        titlePath={titlePathPlus}
                        titleTree={titleTreePlus}
                        expandedPaths={expandedPaths}
                        expandConcept={expandConcept}
                        onAddNewConcept={onAddNewConcept}
                        onDeleteConcept={onDeleteConcept}
                        onAddExistingConcept={onAddExistingConcept}
                        collapseConcept={collapseConcept}
                        schemeIRI={schemeIRI}
                        data={data}
                        onSelect={onSelect}
                        addToScheme={addToScheme}
                        onBreakLink={onBreakLink}
                    />
                </div>
            }
        </div>;
    }
}

Concept.propTypes = {
    schemeIRI: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    viewLang: PropTypes.string,
    version: PropTypes.number,
    graph: PropTypes.string.isRequired,
    foundPath: PropTypes.string,
    path: PropTypes.string.isRequired,
    selectedTitlePath: PropTypes.array,
    titlePath: PropTypes.array,
    expandedPaths: PropTypes.arrayOf(PropTypes.string).isRequired,
    expandConcept: PropTypes.func.isRequired,
    collapseConcept: PropTypes.func.isRequired,
    onAddExistingConcept: PropTypes.func.isRequired,
    onAddNewConcept: PropTypes.func.isRequired,
    onDeleteConcept: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    parent: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool,
    skosXL: PropTypes.bool.isRequired,
    useXL: PropTypes.bool,
    isFullView: PropTypes.bool,
    useWorkflow: PropTypes.bool,
    useSlashIRI: PropTypes.bool,
    useUuidIRI: PropTypes.bool,
    useCounterIRI: PropTypes.bool,
    counters: PropTypes.object,
    useSelect: PropTypes.number,
    titleTree: PropTypes.array,
    project: PropTypes.string,
    foundTime: PropTypes.number,
    useCustomSort: PropTypes.bool,
    customSort: PropTypes.object,
    childProperty: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    showWarning: PropTypes.func.isRequired,
    addToScheme: PropTypes.func.isRequired,
    onBreakLink: PropTypes.func.isRequired,
    classes: PropTypes.object,
    foundRef: PropTypes.object,
    iriOptions: PropTypes.object
};

const cpt = DragSource(Types.CONCEPT, conceptSource, collectSource)(withStyles(styles)(Concept));

export default withGS(cpt, ["ALL"], null, {}, true);